import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { reportNoShow } from "@/lib/no-show.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { noShowClosesAtMs, noShowOpensAtMs, noShowWaitLabel } from "@/lib/no-show-window";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/shift/$id/no-show")({
  head: () => ({ meta: [{ title: "Report No-Show — The Hospitality Reserve" }] }),
  component: NoShowPage,
});

type Shift = {
  id: string; venue_id: string; start_time: string; end_time: string;
  status: string; address: string | null; postcode: string | null;
};
type Booking = { id: string; bartender_id: string; status: string; completed_at: string | null; cancelled_at: string | null; no_show_reported_at: string | null; profile?: { full_name: string | null } };

function NoShowPage() {
  const { id } = useParams({ from: "/_authenticated/shift/$id/no-show" });
  const navigate = useNavigate();
  const submit = useServerFn(reportNoShow);

  const [shift, setShift] = useState<Shift | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string>("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
      setMe(u.user?.id ?? null);
      const { data: s } = await supabase
        .from("shifts")
        .select("id,venue_id,start_time,end_time,status,address,postcode")
        .eq("id", id).maybeSingle();
      setShift(s as Shift | null);
      const { data: bs } = await supabase
        .from("bookings")
        .select("id,bartender_id,status,completed_at,cancelled_at,no_show_reported_at")
        .eq("shift_id", id);
      const rawRows = (bs ?? []) as Array<{ id: string; bartender_id: string; status: string; completed_at: string | null; cancelled_at: string | null; no_show_reported_at: string | null }>;
      const bartIds = Array.from(new Set(rawRows.map((b) => b.bartender_id)));
      const { data: profs } = bartIds.length
        ? await supabase.from("profiles").select("id, full_name").in("id", bartIds)
        : { data: [] };
      const profMap = new Map<string, { full_name: string | null }>(
        ((profs ?? []) as Array<{ id: string; full_name: string | null }>).map((p) => [p.id, { full_name: p.full_name }]),
      );
      const rows: Booking[] = rawRows.map((b) => ({ ...b, profile: profMap.get(b.bartender_id) }));
      setBookings(rows);
      const eligible = rows.find((r) => r.status === "accepted" && !r.completed_at && !r.cancelled_at && !r.no_show_reported_at);
      if (eligible) setBookingId(eligible.id);
    })();
  }, [id]);

  if (!shift) {
    return (
      <div className="min-h-screen bg-background"><ReserveHeader />
        <main className="mx-auto max-w-2xl px-6 py-16 text-sm text-muted-foreground">Loading…</main>
      </div>
    );
  }

  const isVenue = me === shift.venue_id;
  const now = Date.now();
  const opensAt = noShowOpensAtMs(shift.start_time, shift.end_time);
  const inWindow = now >= opensAt && now <= noShowClosesAtMs(shift.end_time);
  const eligibleBookings = bookings.filter((b) => b.status === "accepted" && !b.completed_at && !b.cancelled_at && !b.no_show_reported_at);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookingId) { toast.error("Select the affected booking."); return; }
    if (details.trim().length < 10) { toast.error("Add at least 10 characters of detail."); return; }
    setBusy(true);
    try {
      const r = await submit({ data: { shift_id: id, booking_id: bookingId, environment: getStripeEnvironment(), details: details.trim() } });
      if ("error" in r) throw new Error(r.error);
      if (r.role === "bartender") {
        toast.success(`Reported. Venue refund: £${(r.refund_pence / 100).toFixed(2)}.`);
      } else {
        toast.success(`Reported. Bartender payout queued: £${(r.payout_pence / 100).toFixed(2)}.`);
      }
      navigate({ to: isVenue ? "/venue" : "/bartender" });
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not submit."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role={isVenue ? "venue" : "bartender"} />
      <main className="mx-auto max-w-2xl px-6 py-12">
        <p className="tracking-wide-luxury text-accent">Resolution</p>
        <h1 className="mt-3 font-display text-3xl">Report a No-Show</h1>
        <span className="gold-divider mt-4" />

        <div className="mt-6 border border-border bg-card p-5 text-sm">
          <p className="text-muted-foreground tracking-wide-luxury text-[0.65rem]">Engagement</p>
          <p className="mt-1 font-display">{new Date(shift.start_time).toLocaleString("en-GB")} — {new Date(shift.end_time).toLocaleString("en-GB")}</p>
          {shift.address && <p className="mt-1 text-xs text-muted-foreground">{[shift.address, shift.postcode].filter(Boolean).join(" · ")}</p>}
        </div>

        {!inWindow && (
          <p className="mt-6 text-sm text-destructive">No-shows can only be reported once {noShowWaitLabel(shift.start_time, shift.end_time)} have passed since the start time (25% of the shift, or 30 minutes — whichever is shorter), and up to 6 hours after the shift ends.</p>
        )}

        {inWindow && shift.status !== "paid" && (
          <p className="mt-6 text-sm text-destructive">No-shows can only be reported on paid engagements.</p>
        )}

        {inWindow && shift.status === "paid" && eligibleBookings.length === 0 && (
          <p className="mt-6 text-sm text-muted-foreground">No eligible bookings remain on this engagement.</p>
        )}

        {inWindow && shift.status === "paid" && eligibleBookings.length > 0 && (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5 border border-border bg-card p-6">
            <div>
              <p className="tracking-wide-luxury text-[0.65rem] text-muted-foreground">Who didn't show up?</p>
              <p className="mt-1 text-sm">{isVenue ? "The bartender" : "The venue"}.</p>
            </div>

            {isVenue && eligibleBookings.length > 1 && (
              <div>
                <p className="tracking-wide-luxury text-[0.65rem] text-muted-foreground mb-2">Select bartender</p>
                <div className="space-y-2">
                  {eligibleBookings.map((b) => (
                    <label key={b.id} className={`flex items-center gap-3 border p-3 cursor-pointer ${bookingId === b.id ? "border-accent" : "border-border"}`}>
                      <input type="radio" name="booking" checked={bookingId === b.id} onChange={() => setBookingId(b.id)} />
                      <span>{b.profile?.full_name ?? b.bartender_id.slice(0, 8)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="tracking-wide-luxury text-[0.65rem] text-muted-foreground mb-2">What happened?</p>
              <Textarea required minLength={10} rows={5} value={details} onChange={(e) => setDetails(e.target.value)}
                placeholder="When did you arrive, who did you contact, what was the situation…" />
              <p className="mt-2 text-xs text-muted-foreground">
                {isVenue
                  ? "We'll refund your engagement in full, reopen the slot, add 3 free £5 booking credits to your account, and log a strike against the bartender pending admin review."
                  : "We'll release your full payout to your Connect account and log a strike against the venue pending admin review."}
              </p>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={busy} className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury">
                {busy ? "Submitting…" : "Submit Report"}
              </Button>
              <Link to={isVenue ? "/venue" : "/bartender"}
                className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">
                Cancel
              </Link>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
